import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, ExternalLink, FileText, ArrowUpRight, GraduationCap, Fingerprint } from "lucide-react";
import { api, fileUrl } from "../utils/api";
import { useSettings } from "../context/SettingsContext";
import usePageMeta from "../hooks/usePageMeta";
import Pagination from "../components/Pagination";
import PageMasthead from "../components/PageMasthead";
import { SkeletonRows } from "../components/Skeleton";
import { local } from "../lib/images";
import { fetchOrcidWorks, mergePublications, isValidOrcid } from "../lib/orcid";

const PER_PAGE = 10;

/**
 * A curated list, topped up live from ORCID.
 *
 * Google Scholar remains impossible: it serves X-Frame-Options: DENY and has no
 * public API, so it can be linked but neither embedded nor read. ORCID refuses
 * framing too, but it publishes a free read API — so the works on Dr. Price's
 * record are fetched in the browser and merged into the curated list, newest
 * first, de-duplicated by DOI.
 *
 * The merge direction matters. The curated forty are hand-checked and carry
 * volume and page numbers ORCID does not hold, so they win any collision; ORCID
 * contributes what the curated list has not caught up with yet. See lib/orcid.
 */
function resolveLink(pub) {
  if (pub.external_url) return { href: pub.external_url, kind: "link" };
  if (pub.file_path) return { href: fileUrl(pub.file_path), kind: "document" };
  return null;
}

export default function PublicationsPage() {
  // Two sources, held separately and merged at render.
  //
  // Storing one merged array was a race: whichever request landed second won.
  // ORCID is usually faster than our own Lambda, so it merged into an empty
  // curated list, and the curated response then replaced the whole array —
  // leaving the page showing forty entries while the counter claimed three had
  // come from ORCID. Deriving the list means the order they arrive in cannot
  // matter.
  const [curated, setCurated] = useState([]);
  const [orcidWorks, setOrcidWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef(null);
  const { settings } = useSettings();
  usePageMeta(
    "Selected Publications",
    "Nearly two decades of research across waste management, soils, environmental science, engineering, and the circular economy."
  );

  const orcidHref =
    String(settings.orcid_url || "").trim() ||
    (isValidOrcid(settings.orcid_id) ? `https://orcid.org/${String(settings.orcid_id).trim()}` : "");

  const profiles = [
    { href: settings.scholar_url, label: "Google Scholar", note: "Full citation record, updated continuously", Icon: GraduationCap },
    { href: orcidHref, label: "ORCID", note: "Dr. Price's full works record", Icon: Fingerprint },
  ].filter((x) => String(x.href || "").trim());

  const orcidId = String(settings.orcid_id || "").trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/publications");
        if (!cancelled) setCurated(data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load publications.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ORCID runs as a separate, later effect on purpose. It is an enhancement, not
  // a dependency: the curated list paints as soon as our own API answers, and a
  // slow or unavailable ORCID never delays it or shows an error. A third party
  // having a bad day must not take this page down.
  useEffect(() => {
    if (!isValidOrcid(orcidId)) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const works = await fetchOrcidWorks(orcidId, { signal: controller.signal });
        if (!cancelled) setOrcidWorks(works);
      } catch {
        /* stay with the curated list */
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [orcidId]);

  // Where an entry came from is our problem, not the reader's: the merged list
  // is presented as one record, with no provenance markings.
  const items = useMemo(() => mergePublications(curated, orcidWorks), [curated, orcidWorks]);

  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  // The page lives in the URL so a particular page can be linked and the back
  // button works. Clamped on read, so ?page=99 or a hand-edited URL can't render
  // an empty list.
  const page = Math.min(Math.max(parseInt(searchParams.get("page"), 10) || 1, 1), totalPages);
  const visible = useMemo(
    () => items.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [items, page]
  );

  const goToPage = (n) => {
    const next = Math.min(Math.max(n, 1), totalPages);
    setSearchParams(next === 1 ? {} : { page: String(next) });
    // Paging keeps the same route, so ScrollToTop doesn't fire — bring the top
    // of the list back into view rather than leaving the reader mid-page.
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const firstOnPage = (page - 1) * PER_PAGE + 1;
  const lastOnPage = Math.min(page * PER_PAGE, items.length);

  return (
    <div data-testid="publications-page">
      {/* The analysis lab in the Cox Institute — the elemental analyser, the
          Thermo instrument and the gas cylinders that feed them. The numbers in
          every paper below came off this bench, which is a better argument for
          the page than the stock petri dish that used to sit here.

          The file is stored pre-mirrored: `flip` puts the picture on the left by
          mirroring the container, and a room full of labelled instruments cannot
          be mirrored the way a landscape can. */}
      <PageMasthead
        testId="publications-header"
        flip
        tone="bone"
        image={local("iwm-analysis").src}
        imageSrcSet={local("iwm-analysis").srcSet}
        imageAlt="Elemental analysers and gas cylinders on the bench in the IWM analysis lab"
        eyebrow="§ Index · Selected Publications"
        title={<>The research that <em className="not-italic text-[#A17E3B]">shaped IWM</em>.</>}
        lede="Dr. Gordon Price's publication record reflects nearly two decades of research across waste management, soils, environmental science, engineering, and the circular economy. His work forms the foundation of what IWM is today. Explore his work below."
      />

      {profiles.length > 0 && (
        <section className="border-b hairline bg-[#F2EFEA]" data-testid="publications-profiles">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {profiles.map(({ href, label, note, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between gap-4 border hairline bg-[#F9F8F6] p-6 hover:bg-[#F9F8F6]/60 transition-colors"
                data-testid={`publications-profile-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="flex items-center gap-4 min-w-0">
                  <Icon size={22} strokeWidth={1.25} className="text-[#A17E3B] shrink-0" />
                  <span className="min-w-0">
                    <span className="block font-serif text-xl text-[#1C2722] group-hover:text-[#A17E3B] transition-colors">
                      {label}
                    </span>
                    <span className="block overline mt-1">{note}</span>
                  </span>
                </span>
                <ArrowUpRight size={18} className="text-[#A17E3B] shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            ))}
          </div>
        </section>
      )}

      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          {loading && <SkeletonRows count={6} testId="publications-loading" />}
          {!loading && error && (
            <div className="flex items-center gap-3 py-20 text-[#9B3B26]" data-testid="publications-error">
              <AlertTriangle size={18} /> {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="py-20 text-[#4A5A52]" data-testid="publications-empty">
              No publications listed yet. Please check back soon.
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <>
              <div
                className="flex items-baseline justify-between gap-4 flex-wrap mb-6"
                ref={listRef}
                style={{ scrollMarginTop: "7rem" }}
              >
                <div className="overline" data-testid="publications-count">
                  Showing {firstOnPage}–{lastOnPage} of {items.length}
                </div>
                {totalPages > 1 && (
                  <div className="font-mono text-[11px] tracking-widest uppercase text-[#62706A]">
                    Page {String(page).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
                  </div>
                )}
              </div>
            <ol className="border-t hairline hairline-y reveal" data-testid="publications-list">
              {visible.map((pub, i) => {
                const link = resolveLink(pub);
                const Kind = link?.kind === "document" ? FileText : ExternalLink;
                return (
                  <li key={pub.id} className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-8 px-4 -mx-4 group fade-up transition-colors duration-200 hover:bg-[#F2EFEA]" style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }} data-testid={`publication-${pub.id}`}>
                    <div className="lg:col-span-2 flex items-start gap-3">
                      <span className="font-mono text-[11px] tracking-widest uppercase text-[#7E6229]" data-testid={`publication-year-${pub.id}`}>
                        {pub.year || "—"}
                      </span>
                      {pub.venue && (
                        <span className="text-[11px] font-mono tracking-widest uppercase text-[#62706A] line-clamp-2">{pub.venue}</span>
                      )}
                    </div>
                    <div className="lg:col-span-8">
                      <h3 className="font-serif text-2xl md:text-[28px] leading-snug text-[#1C2722] group-hover:text-[#A17E3B] transition-colors" data-testid={`publication-title-${pub.id}`}>
                        {pub.title}
                      </h3>
                      {pub.authors && (
                        <div className="mt-2 text-[13.5px] italic text-[#4A5A52]" data-testid={`publication-authors-${pub.id}`}>{pub.authors}</div>
                      )}
                      {pub.abstract && (
                        <p className="mt-3 text-[14.5px] leading-relaxed text-[#4A5A52] max-w-3xl" data-testid={`publication-abstract-${pub.id}`}>
                          {pub.abstract}
                        </p>
                      )}
                    </div>
                    <div className="lg:col-span-2 flex lg:justify-end items-start">
                      {link ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-outline !py-2 !px-3 whitespace-nowrap"
                          data-testid={`publication-learnmore-${pub.id}`}
                          data-kind={link.kind}
                        >
                          <Kind size={12} />
                          {link.kind === "document" ? "Open PDF" : "Learn more"}
                          <ArrowUpRight size={12} />
                        </a>
                      ) : (
                        <span className="text-[11px] font-mono tracking-widest uppercase text-[#62706A]">Coming soon</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            <Pagination page={page} totalPages={totalPages} onChange={goToPage}
                        testIdPrefix="publications" label="Publications pages" />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
